import {
  CheckCircleRounded as ActiveIcon,
  HighlightOffRounded as InactiveIcon,
  SaveAltRounded as DownloadIcon,
} from "@mui/icons-material";
import {
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Switch,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import { useState } from "react";
import { useParams } from "react-router-dom";
import * as staticDataService from "services/staticDataService";
import * as utils from "utils";

const ExposureMappings = () => {
  const { companyId } = useParams();
  const [showKey, setShowKey] = useState(false);

  const {
    data: exposures,
    isLoading: isExposuresLoading,
  } = staticDataService.useConfigForCompanyQuery(
    { name: "exposures", companyId },
    { skip: !companyId }
  );

  const {
    data: classifications,
    isLoading: isClassificationsLoading,
  } = staticDataService.useConfigForCompanyQuery(
    { name: "classifications", companyId },
    { skip: !companyId }
  );

  if (isExposuresLoading || isClassificationsLoading) {
    return (
      <Stack alignItems={"center"} padding={5} sx={{ width: "100%" }}>
        <DefaultLoader size={48} />
      </Stack>
    );
  }

  if (
    utils.isEmptyObject(exposures?.measures) ||
    utils.isEmptyObject(classifications?.industryClasses)
  ) {
    return (
      <Stack alignItems={"center"} padding={5} sx={{ width: "100%" }}>
        <Typography sx={{ color: "gray" }}>
          {
            "Exposures and/or classifications JSON is not configured or  does not contain data"
          }
        </Typography>
      </Stack>
    );
  }

  const activeClassifications = (classifications?.industryClasses ?? []).filter(
    (x) => x.isDeactivated === false
  );

  const mappings = (exposures?.measures ?? []).map((measure) => {
    return {
      ...measure,
      classifications: activeClassifications.map((x) => ({
        key: x.key,
        name: x.name,
        active: (
          (exposures?.defaultMeasuresByIndustryClass ?? {})[x.key] ?? []
        ).includes(measure.key),
      })),
    };
  });

  const toCsvString = (classifications, mappings) => {
    const header = [
      "KEY",
      "MEASURE",
      "UNIT",
      classifications.map((c) => c.key),
    ];
    const rows = [
      header.join(","),
      ...mappings.map((m) =>
        [m.key, m.measure, m.unit, m.classifications.map((c) => c.active)].join(
          ","
        )
      ),
    ];
    return rows.join("\n");
  };

  const handleDownload = () => {
    const blob = new Blob([toCsvString(activeClassifications, mappings)], {
      type: "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ExposureMappingsByIndustryClass.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <FormControlLabel
          label={"Show exposure key"}
          labelPlacement={"start"}
          control={
            <Switch checked={showKey} onClick={() => setShowKey(!showKey)} />
          }
        />
        <Button startIcon={<DownloadIcon />} onClick={handleDownload}>
          {"Download CSV"}
        </Button>
      </Stack>
      <TableContainer sx={{ overflowX: "initial" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell />
              {showKey && <TableCell align={"center"}>{"Key"}</TableCell>}
              {activeClassifications.map((classification) => (
                <TableCell
                  key={classification.key}
                  align={"center"}
                  sx={{ maxWidth: "6rem" }}
                >
                  {classification.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((item) => (
              <TableRow key={item.key}>
                <TableCell align={"right"}>
                  {`${item.measure} (${item.unit})`}
                </TableCell>
                {showKey && <TableCell align={"center"}>{item.key}</TableCell>}
                {item.classifications.map((x) => (
                  <TableCell key={x.key} align={"center"}>
                    {x.active ? (
                      <ActiveIcon color={"success"} />
                    ) : (
                      <InactiveIcon sx={{ opacity: 0.25 }} />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const SUMMARIES = [
  {
    key: "EXPOSURE_MAPPINGS",
    label: "Exposure Mappings",
    description: "Default exposures per industry class",
    component: <ExposureMappings />,
  },
];

const ConfigSummary = () => {
  const [activeSummary, setActiveSummary] = useState(SUMMARIES[0].key);
  const activeItem = SUMMARIES.filter((x) => x.key === activeSummary)?.[0];

  return (
    <Component
      title={"Config Summary"}
      subtitle={activeItem.description}
      options={
        <Select
          size={"small"}
          value={activeSummary}
          onChange={(e) => setActiveSummary(e.target.value)}
        >
          {SUMMARIES.map((item) => (
            <MenuItem key={item.key} value={item.key}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      }
    >
      {activeItem?.component ?? <></>}
    </Component>
  );
};

export default ConfigSummary;
